import type { NumberCondition } from "@skilitics/judy";
import { isReferencedValue } from "@skilitics/nautilus-core-designer-data";

import type { ConditionBuilder, ConditionBuilderContext } from "../../../types";
import { isNumberOperator, isTernaryOperator } from "../helpers.ts";
import type { NumericConditionEditorProps, NumericConditionValues } from "../specifiers";
import { NumericConditionEditor, NumericConditionSummary } from "../specifiers";

// note: the "offset" is used to allow the ui range differ from the stored
//       range, for example, visually indexing from 1 but internally from 0

export interface ArrayLengthConditionBuilderContext extends ConditionBuilderContext {
  config: NumericConditionValues;
  spec: Omit<NumericConditionEditorProps, keyof NumericConditionValues | "onChange" | "values"> & {
    property: string;
    type: "number" | "string" | "boolean" | "object";
  };

  // the assumption is that any property referenced by consuming node designer is a number
  result: Record<string, number>;
}

const isValid: NonNullable<ConditionBuilder<ArrayLengthConditionBuilderContext>["isValid"]> = ({ operator, value }) =>
  isNumberOperator(operator) && (Number.isFinite(value) || isReferencedValue(value));

export const arrayLengthConditionBuilder: ConditionBuilder<ArrayLengthConditionBuilderContext> = {
  typeId: "number",
  defaultCategory: "Activity",
  build: ({ config, spec: { property, type }, resultProperty, resolvableValue }) => {
    if (!isValid(config)) return;

    const { operator, value, value2 } = config;

    const count = {
      type: "number",
      resolveType: "count",
      values: resultProperty(property, `${type}Array` as any),
    };

    const condition = {
      type: "number",
      subject: count,
      operator,
    };

    if (isTernaryOperator(operator)) {
      if (value2 === undefined) return;

      const left = resolvableValue(value, "number");
      const right = resolvableValue(value2, "number");
      if (left === undefined || right === undefined) return;

      return { ...condition, left, right } as NumberCondition;
    }

    const object = resolvableValue(value, "number");
    if (object === undefined) return;

    return { ...condition, object } as NumberCondition;
  },
  isValid,
  getInitialConfig: () => ({}),
  views: {
    summary: ({ spec: { title, offset }, config }) => (
      <NumericConditionSummary title={title} offset={offset} values={config} />
    ),
    edit: ({ spec: { title, min, max, formatStringOverrides }, config, onConfigChange }) => (
      <NumericConditionEditor
        title={title}
        min={min}
        max={max}
        formatStringOverrides={formatStringOverrides}
        values={config}
        onChange={onConfigChange}
      />
    ),
  },
};
